import * as THREE from 'three';

import { axialKey, axialToWorld, roundAxial, worldToAxial } from './HexCoordinates';
import { CORNERS, elevationToY } from './HexMetrics';
import { groundLevel, HexCellData, isWater, surfaceAt, waterLevel } from './HexSurface';
import { HALF_WIDTHS, buildRibbonGeometry } from './RiverSurface';

/**
 * Drogi, rysowane wstęgą — tą samą, którą rysuje się rzeka.
 *
 * Droga przez pole **przechodzi**, tak jak rzeka: nie stoi na nim jak budynek,
 * tylko wchodzi jedną krawędzią i wychodzi drugą. To jest ten sam kształt
 * faktu, więc jest to ta sama geometria — łuki, zszywanie ramion i wysokość
 * czytana z powierzchni terenu przyszły z `RiverSurface` bez zmiany.
 *
 * Czym się różni: **maska jest stanem gry, nie warstwą świata**. Rzeka jedzie
 * w paczce chunków i nie zmienia się nigdy; droga przychodzi jak budynki —
 * listą obszaru po HTTP i zdarzeniem po sockecie — i pojawia się w trakcie gry.
 * Klient wpisuje ją do swojej mapy pól (`HexCellData.road`) i przemesza chunk,
 * dokładnie tak, jak robi to po postawieniu budynku.
 */

/** Bity 0–5 maski. Reszta bajtu nie znaczy dziś nic. */
const EDGE_MASK = 0b111111;

/**
 * Połowa szerokości wstęgi, w jednostkach świata.
 *
 * Węższa od najmniejszej rzeki: dróg jest w krajobrazie więcej i mają być
 * kreską, którą widać, a nie pasem, który zasłania pole.
 *
 * **Eksportowana z tego samego powodu, co `HALF_WIDTHS` przy rzece**: drzewa
 * i głazy muszą stanąć obok tego, co przez ich pole przechodzi, a druga kopia
 * tej liczby rozjechałaby się w dniu, w którym ktoś zmieni szerokość drogi —
 * i objawiłoby się to lasem rosnącym na trakcie.
 */
export const ROAD_HALF_WIDTH = 0.055;

export function createRoadMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    // Ubita ziemia: jaśniejsza i bardziej szara od gruntu, na którym leży,
    // żeby czytała się na łące i w lesie tak samo.
    color: 0xa08a66,
    roughness: 0.9,
    metalness: 0,
    // Wstęga dzieli płaszczyznę z terenem pod nią — bez tego dwa trójkąty biją
    // się o każdy piksel i droga miga przy ruchu kamery. To samo, co rzeka.
    //
    // **Słabsze przesunięcie niż rzeka (−2), i to jest cała reguła pierwszeństwa
    // pod mostem.** Na polu przeprawy obie wstęgi leżą na tej samej ziemi;
    // wygrywa ta bliżej kamery, a woda ma płynąć **nad** drogą, bo to droga
    // przechodzi nad rzeką po pokładzie, a nie rzeka pod ziemią.
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
  });
}

/**
 * Wykop: droga, która się jeszcze buduje. Ciemniejsza ziemia, trochę
 * przezroczysta — widać, którędy pobiegnie, i widać, że jeszcze nie biegnie.
 */
export function createDigMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0x6b5237,
    roughness: 1,
    metalness: 0,
    transparent: true,
    opacity: 0.7,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
  });
}

/**
 * Jedna wstęga dróg na chunk, albo `null`, gdy żadne jego pole nic nie niesie.
 *
 * `which` dzieli drogi gotowe od tych w budowie — rysuje się je dwoma
 * materiałami, drogą i wykopem.
 */
export function buildRoadGeometry(
  members: HexCellData[],
  world: Map<number, HexCellData>,
  which: 'all' | 'ready' | 'pending' = 'all'
): THREE.BufferGeometry | null {
  return buildRibbonGeometry(members, world, (cell) => {
    const edges = (cell.road ?? 0) & EDGE_MASK;
    // Droga na wodzie jest mostem i rysuje ją `bridgeSpans`, nie wstęga.
    if (edges === 0 || isWater(cell)) return null;
    if (which === 'ready' && cell.roadPending) return null;
    if (which === 'pending' && !cell.roadPending) return null;
    return { edges, halfWidth: ROAD_HALF_WIDTH };
  });
}

/**
 * Mosty: tam, gdzie droga przecina rzekę.
 *
 * **Przecięcie jest jedynym sposobem, w jaki te dwie wstęgi mogą się spotkać** —
 * droga nie kładzie się wzdłuż rzeki, pilnują tego obie strony — więc pole
 * niosące jedno i drugie jest z definicji przeprawą. Nie ma tu żadnej reguły
 * ponad tą, którą już znamy: jest maska drogi i maska rzeki na jednym polu.
 *
 * Rysowane osobną siatką, nie wstęgą: most jest **nad** wodą, a wstęga leży na
 * ziemi i wygrywa z terenem przesunięciem głębi. Pokład na tej samej wysokości
 * byłby drogą pod wodą.
 */

/** Ile pokład stoi nad gruntem pola. */
export const DECK_LIFT = 0.1;

/**
 * Ile pokład wystaje poza wstęgę wody z każdej strony.
 *
 * **Most jest ściśle nad rzeką**, a nie mostem przez całe pole: przęsło ciągnące
 * się od krawędzi do krawędzi byłoby wiaduktem nad łąką, którą droga i tak
 * przechodzi po ziemi. Zapas jest po to, żeby pokład opierał się o brzeg,
 * a nie kończył w wodzie.
 *
 * **Zapas jest większy od pierwszej wersji (0,05) i to była poprawka z oka.**
 * Przy niej przęsło kończyło się dokładnie tam, gdzie woda, więc most czytał
 * się jako zbyt wąski dla rzeki, którą przykrywa — a przy skosie, gdzie droga
 * przecina nurt pod kątem, woda jest wzdłuż przęsła jeszcze szersza, niż mówi
 * jej klasa. Nadmiar idzie w brzeg, na którym most i tak ma się opierać, więc
 * kosztuje tylko tyle, że nad najwęższą strugą oparcie jest długie względem
 * samej wody.
 */
const DECK_MARGIN = 0.09;

const DECK_THICKNESS = 0.03;
/**
 * Pokład szerszy od drogi, żeby było widać, że to przeprawa, a nie kreska.
 *
 * **Eksportowana z tego samego powodu, co `ROAD_HALF_WIDTH`**: model mostu jest
 * do niej dopasowywany na szerokość, więc dwie kopie tej liczby rozjechałyby
 * się w dniu, w którym ktoś zmieni jedną — a objawiłoby się to przeprawą
 * węższą albo szerszą od pokładu, który stoi na sąsiednim skrzyżowaniu.
 */
export const DECK_HALF_WIDTH = 0.09;

const RAIL_HEIGHT = 0.07;
const RAIL_THICKNESS = 0.018;

export function createBridgeMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    // Drewno: cieplejsze i ciemniejsze od ubitej ziemi drogi, żeby przeprawa
    // czytała się jako co innego niż to, co do niej dochodzi.
    color: 0x8a6440,
    roughness: 0.85,
    metalness: 0,
    flatShading: true,
  });
}

/**
 * Mosty jednego chunka w jednym buforze, albo `null`, gdy żadnego tu nie ma.
 *
 * Pole z dwoma ramionami dostaje jeden pokład z brzegu na brzeg — bo tym most
 * jest. Skrzyżowanie z trzema i więcej dostaje pokład na każde ramię, od środka
 * pola: przeprawa rozgałęziona nie ma jednej osi, a zgadywanie, która jest
 * główna, byłoby regułą o niczym.
 *
 * `skipSimple` oddaje proste przęsła modelowi (`BridgeModel.ts`) i zostawia tej
 * funkcji same skrzyżowania. Domyślnie fałsz, bo bez pliku wstęga jest wszystkim,
 * co przeprawa ma — to ta sama zasada, co przy budynkach bez modelu.
 */
export function buildBridgeGeometry(
  members: HexCellData[],
  skipSimple = false
): THREE.BufferGeometry | null {
  const parts: THREE.BufferGeometry[] = [];

  for (const span of bridgeSpans(members)) {
    if (skipSimple && span.simple) continue;
    parts.push(...plank(span.x, span.y + DECK_LIFT, span.z, span.a, span.b));
  }

  if (parts.length === 0) return null;

  const merged = weld(parts);
  for (const part of parts) part.dispose();
  return merged;
}

/** Jedno przęsło: gdzie leży i między czym a czym idzie. */
export interface BridgeSpan {
  /** Środek pola w świecie. `y` to **grunt**, nie pokład — ten stoi wyżej. */
  x: number;
  y: number;
  z: number;
  /** Końce przęsła, względem środka pola. */
  a: { x: number; z: number };
  b: { x: number; z: number };
  /**
   * Czy pole jest prostą przeprawą: dwa ramiona, jedno przęsło z brzegu na
   * brzeg. Skrzyżowanie ma ich kilka i żadne z nich nie jest osią — stąd to
   * pole, a nie liczenie ramion drugi raz po stronie tego, kto pyta.
   */
  simple: boolean;
}

/**
 * Przęsła, jakie niosą pola tego chunka.
 *
 * **Wydzielone, bo pytają o to dwie rzeczy**: wstęga, która buduje pokład
 * z pudełek, i model, który staje na prostym przęśle. Druga kopia tego rachunku
 * byłaby bliźniaczą regułą — a rozjechałaby się cicho, bo obie strony wyglądają
 * na działające dopóty, dopóki patrzy się na jedną.
 */
export function bridgeSpans(members: HexCellData[]): BridgeSpan[] {
  const spans: BridgeSpan[] = [];

  for (const cell of members) {
    const road = (cell.road ?? 0) & EDGE_MASK;
    const river = (cell.river ?? 0) & EDGE_MASK;
    // **Most na wodzie** — morzu albo jeziorze — to droga na polu wody. Stoi na
    // lustrze, nie na dnie, i sięga od krawędzi do krawędzi pola: po obu
    // stronach jest ląd, a woda zajmuje całe pole.
    const onWater = isWater(cell);
    if (road === 0 || (river === 0 && !onWater)) continue;

    const { x, z } = axialToWorld(cell.q, cell.r);
    // Pole z drogą jest wypłaszczone, więc jego wysokość to jedna liczba, a nie
    // powierzchnia do próbkowania.
    const y = elevationToY(onWater ? (waterLevel(cell) ?? 0) : groundLevel(cell));

    // Jak szeroka jest tu woda — stąd bierze się długość przęsła. Klasa
    // wielkości siedzi w bitach 6–7 bajtu rzeki, tak jak przy jej rysowaniu.
    // Na wodzie przęsło sięga krawędzi pola (`Infinity` — ramię urywa się na
    // środku krawędzi niżej).
    const reach = onWater
      ? Infinity
      : HALF_WIDTHS[((cell.river ?? 0) >> 6) & 0b11] + DECK_MARGIN;

    const arms: { x: number; z: number }[] = [];
    for (let edge = 0; edge < 6; edge += 1) {
      if ((road & (1 << edge)) === 0) continue;
      const midX = (CORNERS[edge].x + CORNERS[edge + 1].x) / 2;
      const midZ = (CORNERS[edge].z + CORNERS[edge + 1].z) / 2;
      // Kierunek ramienia, nie jego koniec: przęsło ma sięgać brzegu wody,
      // a nie krawędzi pola.
      const length = Math.hypot(midX, midZ) || 1;
      const arm = Math.min(reach, length);
      arms.push({ x: (midX / length) * arm, z: (midZ / length) * arm });
    }
    if (arms.length === 0) continue;

    // Dwa ramiona to jedno przęsło z brzegu na brzeg — i tym most jest.
    // Skrzyżowanie z trzema i więcej dostaje przęsło na każde ramię, od środka
    // pola: przeprawa rozgałęziona nie ma jednej osi, a zgadywanie, która jest
    // główna, byłoby regułą o niczym.
    const simple = arms.length === 2;
    const pairs: [{ x: number; z: number }, { x: number; z: number }][] = simple
      ? [[arms[0], arms[1]]]
      : arms.map((arm) => [{ x: 0, z: 0 }, arm] as [typeof arm, typeof arm]);

    for (const [a, b] of pairs) {
      spans.push({ x, y, z, a, b, simple });
    }
  }

  return spans;
}

/** Pokład i dwie barierki wzdłuż jednego przęsła. */
function plank(
  x: number,
  y: number,
  z: number,
  a: { x: number; z: number },
  b: { x: number; z: number }
): THREE.BufferGeometry[] {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const length = Math.hypot(dx, dz);
  if (length < 1e-6) return [];

  const angle = Math.atan2(dz, dx);
  const midX = x + (a.x + b.x) / 2;
  const midZ = z + (a.z + b.z) / 2;

  const pieces: THREE.BufferGeometry[] = [];

  const deck = new THREE.BoxGeometry(length, DECK_THICKNESS, DECK_HALF_WIDTH * 2);
  deck.rotateY(-angle);
  deck.translate(midX, y, midZ);
  pieces.push(deck);

  // W poprzek przęsła, żeby barierki stanęły na jego brzegach.
  //
  // **To była pomyłka warta zapisania.** Pudełko obrócone o `-angle` wokół Y
  // ma swoją oś Z tam, gdzie `(−sin angle, cos angle)` — a pierwsza wersja
  // liczyła `(sin angle, cos angle)`, czyli wektor odbity względem osi Z.
  // Dla przęsła leżącego wzdłuż X (sinus zero) wychodziło dobrze, i dlatego
  // wyglądało na działające; przy każdym innym kącie barierki rozjeżdżały się
  // z pokładem tym bardziej, im bliżej było do prostopadłej.
  const acrossX = -Math.sin(angle);
  const acrossZ = Math.cos(angle);

  for (const side of [-1, 1]) {
    const rail = new THREE.BoxGeometry(length, RAIL_HEIGHT, RAIL_THICKNESS);
    rail.rotateY(-angle);
    rail.translate(
      midX + acrossX * side * DECK_HALF_WIDTH,
      y + RAIL_HEIGHT / 2,
      midZ + acrossZ * side * DECK_HALF_WIDTH
    );
    pieces.push(rail);
  }

  return pieces;
}

/**
 * Kilka brył w jeden bufor.
 *
 * Napisane tutaj, a nie wzięte z `three-stdlib`: to jest sklejenie tablic
 * pozycji, normalnych i indeksów, a zależność za to byłaby zależnością za nic —
 * ten sam rachunek, który `BuildingShapes` robi dla sylwetek.
 */
function weld(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const positions: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];

  for (const part of parts) {
    const offset = positions.length / 3;
    const position = part.getAttribute('position');
    const normal = part.getAttribute('normal');
    const index = part.getIndex();

    for (let i = 0; i < position.count; i += 1) {
      positions.push(position.getX(i), position.getY(i), position.getZ(i));
      normals.push(normal.getX(i), normal.getY(i), normal.getZ(i));
    }

    if (index) {
      for (let i = 0; i < index.count; i += 1) indices.push(offset + index.getX(i));
    } else {
      for (let i = 0; i < position.count; i += 1) indices.push(offset + i);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setIndex(indices);
  geometry.computeBoundingSphere();
  return geometry;
}

/**
 * Wysokość pokładu mostu na tym polu, w jednostkach świata — albo nic, gdy
 * pole nie jest wodą z mostem. Pokład leży tam, gdzie pokład z pudełek:
 * `DECK_LIFT` nad lustrem wody.
 */
export function deckY(cell: HexCellData | undefined): number | null {
  if (!cell || !isWater(cell) || !((cell.road ?? 0) & EDGE_MASK)) return null;
  return elevationToY(waterLevel(cell) ?? 0) + DECK_LIFT;
}

/**
 * Po czym się tu chodzi albo płynie, w jednostkach świata: pokład mostu na polu
 * wody z mostem, **lustro na gołej wodzie**, a wszędzie indziej powierzchnia
 * terenu. `null`, gdy pola nie ma.
 *
 * Bez pierwszego pionek na moście szedł po dnie — wysokość pola wody to dno,
 * a nie lustro, i powierzchnia terenu też jest tam dnem. **Bez drugiego po dnie
 * szła trasa statku i pionek wsiadający na pokład**: jedno i drugie przechodzi
 * przez wodę, po której nikt dotąd nie chodził, więc pytanie nigdy nie padało.
 *
 * Nic się przez to nie psuje tam, gdzie było dobrze: na wodę bez mostu nie
 * wchodzi nikt pieszo, więc jedyne, co tę odpowiedź czyta, to rzeczy, które na
 * wodzie **mają** być — trasa po morzu i ten, kto właśnie wsiada.
 */
export function walkY(world: Map<number, HexCellData>, x: number, z: number): number | null {
  const fractional = worldToAxial(x, z);
  const cell = roundAxial(fractional.q, fractional.r);
  const data = world.get(axialKey(cell.q, cell.r));
  const deck = deckY(data);
  if (deck !== null) return deck;
  if (data && isWater(data)) return elevationToY(waterLevel(data) ?? 0);
  const ground = surfaceAt(world, x, z);
  return ground === null ? null : elevationToY(ground);
}
